import { CSSProperties, useMemo, useState } from 'react';
import { EmptyListPlaceholderComponent } from '@vivli/shared/components';
import { useDataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { AuditTrailContextualComponent } from '@vivli/features/admin-console/components';
import { AssetsConstant } from '@vivli/shared/infrastructure/constants';
import { HistoryListView, LoadIndicatorCenteredView } from '@vivli/shared/views';
import { finalize, first } from 'rxjs/operators';
import {
    useActiveUserContext,
    useDataRequestPermissionsContext,
    useDataRequestsApiContext,
    useModalApiContext,
    useToastApiContext,
} from '@vivli/core/infrastructure/context';
import { downloadFile, useErrorHandling } from '@vivli/shared/infrastructure/hook';
import { HistoryActionEnum, PermissionsEnum } from '@vivli/shared/infrastructure/enum';

const placeholderStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 2,
};

const auditButtonStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'flex-start',
    paddingRight: '60px',
    paddingBottom: '20px',
};


export const DataRequestHistoryComponent = () => {
    const _toastService = useToastApiContext();
    const _modalService = useModalApiContext();
    const { handleError } = useErrorHandling();
    const { dataRequest, organizations } = useDataRequestContext();
    const { shouldHideReviewers } = useDataRequestPermissionsContext();
    const dataRequestService = useDataRequestsApiContext();
    const hideReviewers = shouldHideReviewers(dataRequest, organizations);
    const { hasAnyPermission } = useActiveUserContext();
    const [isLoading, setIsLoading] = useState(false);

    const amendedHistoryEntries = useMemo(() => {
        let entries = dataRequest.dataRequestHistoryEntries?.map((entry) => {
            const file = entry.comment ? entry.comment.includes(': DrSnapshot') : false;
            entry.onClick = file ? () => downloadPdfVersion(entry.comment) : undefined;
            entry.clicked = file ? false : undefined;
            return entry;
        });
        //hide old PDF enties
        entries = entries?.filter(
            (entry) => entry.action !== HistoryActionEnum.PdfVersionCreated || !entry.comment?.includes(': DrPrintView')
        );
        return entries;
    }, [dataRequest.dataRequestHistoryEntries]);

    const displayOverTheLimitMessage = () => {
        _modalService.error('You cannot select more than 2 rows.');
    };
    const downloadPdfVersion = (comment: string) => {
        _toastService.info('Preparing file for download...');
        let fileName = comment.split('DrSnapshot')[1].split('.pdf')[0];
        fileName = `DrSnapshot${fileName}.pdf`;
        dataRequestService
            .downloadPdfVersion(dataRequest.id, fileName)
            .pipe(first())
            .subscribe(
                (downloadLink) => {
                    downloadFile(downloadLink, fileName);
                },
                (error) => {
                    handleError('Unable to download file.', error);
                }
            );
    };

    const downloadComparison = (from: string, to: string) => {
        setIsLoading(true);
        dataRequestService
            .downloadPdfComparison(dataRequest.id, from, to)
            .pipe(
                first(),
                finalize(() => setIsLoading(false))
            )
            .subscribe((data) => {
                downloadFile(
                    'data:application/pdf;base64,' + data,
                    `DataRequest-${dataRequest.id}-comparison-${new Date().toDateString().replace(/ /g, '_')}.pdf`
                );
            }, handleError);
    };

    if (!dataRequest || !organizations) {
        return <LoadIndicatorCenteredView />;
    }

    return (
        <>
            <div style={auditButtonStyle}>
                <AuditTrailContextualComponent
                    type="DataRequest"
                    id={dataRequest.dataRequestDoi}
                    internalId={dataRequest.id}
                ></AuditTrailContextualComponent>
            </div>
            {dataRequest.dataRequestHistoryEntries?.length > 0 ? (
                <div style={{ marginLeft: 51, paddingRight: 101, height: '550px', width: '100%' }}>
                    <HistoryListView
                        historyEntries={amendedHistoryEntries}
                        hideReviewers={hideReviewers}
                        includeOrgName={false}
                        showExportBtn={true}
                        hideFloatingFilter={false}
                        exportFileName={`data-request-history-${
                            dataRequest.dataRequestDoi ? dataRequest.dataRequestDoi : dataRequest.id
                        }-${new Date().toISOString()}.csv`}
                        maxClicked={2}
                        displayOverTheLimitMessage={displayOverTheLimitMessage}
                        downloadComparison={downloadComparison}
                        isLoading={isLoading}
                        autoHeight={false}
                    />
                </div>
            ) : (
                <EmptyListPlaceholderComponent
                    optionalStyles={placeholderStyle}
                    imageHeight={'100%'}
                    image={AssetsConstant.TRIALS_ICON_INACTIVE}
                    text={'There is no history for this data request because it has not yet been submitted.'}
                />
            )}
        </>
    );
};
